/**
 * @file 校区管理页
 * @description 侧栏「校区管理」：页头操作、接口列表与行操作。
 *
 * @whenToUse 路由 `/`。
 * @whenNotToUse 其他业务菜单页。
 */

import { useRef, useState } from 'react'
import { MoreHorizontal, Plus } from 'lucide-react'
import { confirm } from '@/components/ui/confirm.tsx'
import { Button } from '@/components/ui/button.tsx'
import { type DataTableColumn } from '@/components/ui/data-table.tsx'
import { Input } from '@/components/ui/input.tsx'
import { RequestTable, type RequestTableHandle } from '@/components/ui/request-table.tsx'
import { DropdownMenu } from '@/components/ui/dropdown-menu.tsx'
import { IconButton } from '@/components/ui/icon-button.tsx'
import { PageHeader } from '@/components/ui/page-header.tsx'
import { toast } from '@/components/ui/toast.tsx'
import {
  CampusRechargeDialog,
  CampusRechargeRecordsDialog,
  type CampusPointsDialogMode,
} from '@/components/pages/campus-page/campus-action-dialogs.tsx'
import { CampusFormDialog } from '@/components/pages/campus-page/campus-form-dialog.tsx'
import { CampusSubOverview } from '@/components/pages/campus-page/campus-sub-overview.tsx'
import {
  changeCampusPoints,
  changeCampusStatus,
  createCampus,
  deleteCampus,
  fetchCampusList,
  resetCampusPwd,
  updateCampus,
  type CampusItem,
} from '@/api/campus.ts'
import { useUserStore } from '@/stores/user-store.ts'

/** 列表查询条件（搜索表单草稿） */
type CampusQuery = {
  /** 校区名称关键字 */
  campusName: string
  /** 主账号关键字 */
  userName: string
}

const INITIAL_QUERY: CampusQuery = { campusName: '', userName: '' }

export function CampusPage() {
  const nickName = useUserStore((s) => s.user?.nickName)
  const isSubAccount = useUserStore((s) => s.user?.isMain) === '0'
  const tableRef = useRef<RequestTableHandle>(null)

  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<CampusItem | null>(null)
  const [active, setActive] = useState<CampusItem | null>(null)
  const [pointsOpen, setPointsOpen] = useState(false)
  const [pointsMode, setPointsMode] =
    useState<CampusPointsDialogMode>('recharge')
  const [recordsOpen, setRecordsOpen] = useState(false)

  /** 行操作成功后刷新列表 */
  const refresh = () => tableRef.current?.refresh()

  const openCreate = () => {
    setEditing(null)
    setFormOpen(true)
  }

  const openEdit = (row: CampusItem) => {
    setEditing(row)
    setFormOpen(true)
  }

  const handleFormSubmit = async (payload: {
    id?: string
    campusName: string
    userName: string
    points: number
    password?: string
  }) => {
    try {
      if (editing) {
        await updateCampus({ ...payload, id: editing.id })
        toast.success('已保存校区', payload.campusName)
      } else {
        await createCampus(payload)
        toast.success('已创建校区', payload.campusName)
      }
      refresh()
    } catch (error) {
      toast.danger(error instanceof Error ? error.message : '保存失败')
    }
  }

  const toggleEnabled = async (row: CampusItem) => {
    const nextStatus = row.status === '1' ? '0' : '1'
    const enabling = nextStatus === '1'
    const ok = await confirm({
      title: enabling ? '启用校区？' : '禁用校区？',
      description: enabling
        ? `启用后「${row.campusName}」可正常登录使用，确认启用？`
        : `禁用后「${row.campusName}」将无法登录使用，确认禁用？`,
      confirmText: enabling ? '启用' : '禁用',
      danger: !enabling,
    })
    if (!ok) return
    try {
      await changeCampusStatus(row.id, nextStatus)
      toast.success(nextStatus === '1' ? '已启用' : '已禁用', row.campusName)
      refresh()
    } catch (error) {
      toast.danger(error instanceof Error ? error.message : '操作失败')
    }
  }

  const handleDelete = async (row: CampusItem) => {
    const ok = await confirm({
      title: '删除校区？',
      description: `将删除「${row.campusName}」，此操作不可恢复。`,
      confirmText: '删除',
      danger: true,
    })
    if (!ok) return
    try {
      await deleteCampus(row.id)
      toast.success('已删除', row.campusName)
      refresh()
    } catch (error) {
      toast.danger(error instanceof Error ? error.message : '删除失败')
    }
  }

  const handleResetPassword = async (row: CampusItem) => {
    const ok = await confirm({
      title: '重置密码？',
      description: `将「${row.campusName}」主账号 ${row.userName} 的密码重置为 123456，确认是否重置？`,
      confirmText: '重置',
    })
    if (!ok) return
    try {
      await resetCampusPwd(row.id)
      toast.success('密码已重置为 123456', row.campusName)
    } catch (error) {
      toast.danger(error instanceof Error ? error.message : '重置失败')
    }
  }

  const openPointsDialog = (row: CampusItem, mode: CampusPointsDialogMode) => {
    setActive(row)
    setPointsMode(mode)
    setPointsOpen(true)
  }

  const handlePointsConfirm = async (points: number, remark: string) => {
    if (!active) return
    const delta = pointsMode === 'deduct' ? -points : points
    try {
      await changeCampusPoints(active.id, delta, remark)
      toast.success(
        pointsMode === 'deduct' ? '扣除成功' : '充值成功',
        `${delta > 0 ? '+' : ''}${delta} 点`,
      )
      refresh()
    } catch (error) {
      toast.danger(error instanceof Error ? error.message : '操作失败')
    }
  }

  const columns: DataTableColumn<CampusItem>[] = [
    {
      key: 'campusName',
      title: '校区名称',
      dataIndex: 'campusName',
      minWidth: 120,
    },
    {
      key: 'userName',
      title: '主账号',
      dataIndex: 'userName',
      minWidth: 120,
    },
    {
      key: 'points',
      title: '点数',
      dataIndex: 'points',
      minWidth: 100,
    },
    {
      key: 'teacherCount',
      title: '教师人数',
      dataIndex: 'teacherCount',
      minWidth: 100,
    },
    {
      key: 'studentCount',
      title: '学员人数',
      dataIndex: 'studentCount',
      minWidth: 100,
    },
    {
      key: 'status',
      title: '状态',
      dataIndex: 'status',
      minWidth: 80,
      render: (row) =>
        row.status === '1' ? (
          <span className="text-success">正常</span>
        ) : (
          <span className="text-muted">禁用</span>
        ),
    },
    {
      key: 'createTime',
      title: '创建时间',
      dataIndex: 'createTime',
      minWidth: 180,
    },
    {
      key: 'actions',
      title: '操作',
      fixed: 'right',
      width: 72,
      render: (row) => (
        <DropdownMenu
          align="end"
          trigger={
            <IconButton
              variant="ghost"
              aria-label={`${row.campusName} 操作`}
              icon={<MoreHorizontal className="size-4" aria-hidden />}
            />
          }
          items={[
            {
              key: 'reset',
              label: '重置密码',
              onSelect: () => {
                void handleResetPassword(row)
              },
            },
            {
              key: 'toggle',
              label: row.status === '1' ? '禁用' : '启用',
              onSelect: () => {
                void toggleEnabled(row)
              },
            },
            {
              key: 'edit',
              label: '编辑',
              onSelect: () => openEdit(row),
            },
            { key: 'div-2', type: 'divider' },
            {
              key: 'recharge',
              label: '充值',
              onSelect: () => openPointsDialog(row, 'recharge'),
            },
            {
              key: 'deduct',
              label: '扣除',
              disabled: row.points <= 100,
              onSelect: () => openPointsDialog(row, 'deduct'),
            },
            {
              key: 'records',
              label: '充值记录',
              onSelect: () => {
                setActive(row)
                setRecordsOpen(true)
              },
            },
            { key: 'div-1', type: 'divider' },
            {
              key: 'delete',
              label: '删除',
              danger: true,
              onSelect: () => {
                void handleDelete(row)
              },
            },
          ]}
        />
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title="校区管理"
        description={
          isSubAccount
            ? '查看本校区教师、学员与点数概况。'
            : '管理子校区账号、点数与启用状态。'
        }
        extra={
          isSubAccount ? undefined : (
            <Button
              icon={<Plus className="size-4" aria-hidden />}
              onClick={openCreate}
            >
              创建校区
            </Button>
          )
        }
      />

      <div className="mt-4">
        {isSubAccount ? (
          <CampusSubOverview displayName={nickName} />
        ) : (
          <RequestTable
            ref={tableRef}
            request={fetchCampusList}
            columns={columns}
            searchForm={{
              initialValues: INITIAL_QUERY,
              items: ({ values, setField }) => (
                <>
                  <Input
                    value={values.campusName}
                    placeholder="校区名称"
                    onChange={(event) => setField('campusName', event.target.value)}
                  />
                  <Input
                    value={values.userName}
                    placeholder="主账号"
                    onChange={(event) => setField('userName', event.target.value)}
                  />
                </>
              ),
            }}
          />
        )}
      </div>

      {/* 创建/编辑校区对话框 */}
      <CampusFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        editing={editing}
        onSubmit={handleFormSubmit}
      />

      {/* 充值 / 扣除对话框 */}
      <CampusRechargeDialog
        open={pointsOpen}
        onOpenChange={setPointsOpen}
        campus={active}
        mode={pointsMode}
        onConfirm={handlePointsConfirm}
      />

      {/* 充值记录对话框 */}
      <CampusRechargeRecordsDialog
        open={recordsOpen}
        onOpenChange={setRecordsOpen}
        campus={active}
      />
    </>
  )
}
